import React from 'react';

/**
 * App-standard table shell: uppercase `ink-muted` headers and a `ghost-bg`
 * row hover (see epic design_html "table styles"). Each piece forwards the
 * rest of its native element's attributes (e.g. `data-testid`, `colSpan`,
 * `onClick`) so callers don't need to re-roll the shell for row/cell-level
 * hooks.
 */
export const Table: React.FC<React.TableHTMLAttributes<HTMLTableElement> & { children: React.ReactNode }> = ({
  children,
  className = '',
  ...rest
}) => (
  <table className={`w-full text-left text-sm ${className}`} {...rest}>{children}</table>
);

export const TableHead: React.FC<React.HTMLAttributes<HTMLTableSectionElement> & { children: React.ReactNode }> = ({
  children,
  className = '',
  ...rest
}) => (
  <thead className={className} {...rest}>{children}</thead>
);

export const TableHeaderCell: React.FC<React.ThHTMLAttributes<HTMLTableCellElement> & { children: React.ReactNode }> = ({
  children,
  className = '',
  ...rest
}) => (
  <th className={`border-b border-line px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-muted ${className}`} {...rest}>
    {children}
  </th>
);

export const TableBody: React.FC<React.HTMLAttributes<HTMLTableSectionElement> & { children: React.ReactNode }> = ({
  children,
  className = '',
  ...rest
}) => (
  <tbody className={className} {...rest}>{children}</tbody>
);

export const TableRow: React.FC<React.HTMLAttributes<HTMLTableRowElement> & { children: React.ReactNode }> = ({
  children,
  className = '',
  ...rest
}) => (
  <tr className={`border-b border-line transition-colors last:border-0 hover:bg-ghost-bg ${className}`} {...rest}>
    {children}
  </tr>
);

export const TableCell: React.FC<React.TdHTMLAttributes<HTMLTableCellElement> & { children: React.ReactNode }> = ({
  children,
  className = '',
  ...rest
}) => (
  <td className={`px-4 py-3 text-ink ${className}`} {...rest}>{children}</td>
);
